// src/api/useFetchObjects.js
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API_BASE_URL } from "../config";

const useFetchObjects = (queryKey, endpoint, token, options = {}) => {
  // Instead of early return, we'll modify how the query function works
  const isInvalidQuery = !queryKey;
  
  const fetchFunction = async () => {
    // If the query key is invalid, return empty data without making a request
    if (isInvalidQuery) {
      console.log("Warning: null or undefined queryKey passed to useFetchObjects, skipping request");
      return { success: true, data: [], count: 0, total: 0 };
    }

    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    // Convert endpoint from object to query string if it's an object
    let finalEndpoint = endpoint;
    if (typeof endpoint === 'object') {
      // Extract the base endpoint (the path without parameters)
      const baseEndpoint = queryKey;
      
      // Create URLSearchParams from the endpoint object
      const params = new URLSearchParams();
      Object.entries(endpoint).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, value);
        }
      });
      
      // Combine base endpoint with query parameters
      const queryString = params.toString();
      finalEndpoint = `${baseEndpoint}${queryString ? `?${queryString}` : ''}`;
    }
    
    console.log("Fetching from:", `${API_BASE_URL}/${finalEndpoint}`);
    
    const response = await axios.get(
      `${API_BASE_URL}/${finalEndpoint}`,
      { headers }
    );
    
    // Unwrap any `{ success, data }` envelope coming from backend
    const respData = response.data;
    if (respData && typeof respData === 'object' && Object.prototype.hasOwnProperty.call(respData, 'data')) {
      return respData.data;
    }
    return respData;
  };

  // Always call useQuery, but disable it when isInvalidQuery is true or token is not available
  const { data, isLoading, isError, isSuccess, error, refetch } = useQuery({
    queryKey: isInvalidQuery ? ['invalid_query'] : [queryKey, endpoint],
    enabled: !isInvalidQuery && !!token,
    queryFn: fetchFunction,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    ...options
  });

  return { 
    data: isInvalidQuery ? null : data, 
    isLoading: isInvalidQuery ? false : isLoading, 
    isError: isInvalidQuery ? false : isError, 
    isSuccess: isInvalidQuery ? true : isSuccess, 
    error: isInvalidQuery ? null : error, 
    refetch: isInvalidQuery ? () => Promise.resolve(null) : refetch 
  };
};

export default useFetchObjects;
